import {useData} from "@hps/hops-react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import GlobalStyles from "@mui/material/GlobalStyles";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
	filterAdornmentButtonStyle,
	filterControlStyle,
	filterRowSx
} from "Raildays/Components/RaildaysFilterStyles.js";
import RaildaysMap from "Raildays/Components/RaildaysMap.js";
import RaildaysScrollTopButton from "Raildays/Components/RaildaysScrollTopButton.js";
import {
	formatDistanceMilesKm,
	resolveExternalUrl,
	resolveRailwayLocation,
	resolveRailwayMediaUrl,
	resolveRailwayType,
	resolveRailwayTypeColor,
	slugify
} from "Raildays/RaildaysUtils.js";
import {useCallback, useMemo, useRef, useState} from "react";
import {useOutletContext} from "react-router-dom";

import Button from "Components/Button.js";
import Card from "Components/Card.js";
import CardSection from "Components/Container.js";
import IconButton from "Components/IconButton.js";
import Image from "Components/Image.js";
import Link from "Components/Link.js";
import Loader from "Components/Loader.js";
import Select from "Components/Select.js";
import TextField from "Components/TextField.js";
import RaildaysService from "Services/RaildaysService.js";

import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import XIcon from "@mui/icons-material/X";

const LETTERS = ["All", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("")];
const MAP_HEIGHT = "calc(100vh - var(--raildays-content-top))";
const CARD_BODY_PADDING = "clamp(16px, 2.2vw, 32px)";

const resolveSocialUrl = (base, handle) => {
	if (!handle) return "";
	if (handle.startsWith("http")) return handle;
	return `${base}${handle.replace(/^@/, "")}`;
};

const normalizePostcode = value => (value || "").toString().toUpperCase().replace(/\s+/g, "");

const haversineMiles = (pointA, pointB) => {
	if (!pointA || !pointB) return null;
	const toRad = deg => (deg * Math.PI) / 180;
	const lat1 = pointA.lat;
	const lon1 = pointA.lng;
	const lat2 = pointB.lat;
	const lon2 = pointB.lng;
	if ([lat1, lon1, lat2, lon2].some(val => Number.isNaN(val))) return null;
	const R = 3958.8;
	const dLat = toRad(lat2 - lat1);
	const dLon = toRad(lon2 - lon1);
	const a =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
};

const getRailwayLatLng = railway => {
	const location = railway?.Location;
	const latitude = Number(location?.Latitude);
	const longitude = Number(location?.Longitude);
	if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
	return {lat: latitude, lng: longitude};
};

const getContrastText = hexColor => {
	if (!hexColor) return "#111827";
	const color = hexColor.replace("#", "");
	const r = parseInt(color.substring(0, 2), 16) || 0;
	const g = parseInt(color.substring(2, 4), 16) || 0;
	const b = parseInt(color.substring(4, 6), 16) || 0;
	const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
	return luminance > 0.7 ? "#111827" : "#fff";
};

const resolveRailwayId = railway => (
	railway?.Id || slugify(railway?.Name || "railway")
);

const resolveRailwayMapMarkerId = railway => {
	const railwayId = resolveRailwayId(railway);
	const coords = getRailwayLatLng(railway);
	if (!coords) return String(railwayId);
	return `${railwayId}-${coords.lat}-${coords.lng}`;
};

const RaildaysRailwaysView = () => {
	const [typeFilter, setTypeFilter] = useState("All");
	const [letterFilter, setLetterFilter] = useState("All");
	const [isLetterFilterApplied, setIsLetterFilterApplied] = useState(false);

	const [postcode, setPostcode] = useState("");
	const [postcodeError, setPostcodeError] = useState("");
	const locationContext = useOutletContext() || {};
	const targetLocation = locationContext.targetLocation || null;
	const setTargetLocation = locationContext.setTargetLocation || (() => {});
	const locationLoading = Boolean(locationContext.locationLoading);
	const setLocationLoading = locationContext.setLocationLoading || (() => {});
	const requestCurrentLocation = locationContext.requestCurrentLocation || (() => {});

	const [expandedId, setExpandedId] = useState(null);

	const mapRef = useRef(null);
	const markerRefs = useRef({});

	const {data: railwaysData, error: railwaysError, loading: railwaysLoading} =
		useData(useCallback(
			() => RaildaysService.getRailways(),
			[]
		));

	const railways = useMemo(() => {
		const resolved = railwaysData;
		if (!resolved) return [];
		if (Array.isArray(resolved)) return resolved;
		if (Array.isArray(resolved.objects)) return resolved.objects;
		return [];
	}, [railwaysData]);

	const typeOptions = useMemo(() => {
		const types = new Map();
		railways.forEach(railway => {
			const typeName = resolveRailwayType(railway);
			if (!typeName) return;
			const slug = slugify(typeName);
			if (!types.has(slug)) {
				types.set(slug, {
					slug,
					name: typeName
				});
			}
		});
		return Array.from(types.values()).sort((a, b) => a.name.localeCompare(b.name));
	}, [railways]);

	const filteredRailways = useMemo(() => {
		return railways.filter(railway => {
			if (typeFilter !== "All") {
				const railwayTypeSlug = slugify(resolveRailwayType(railway));
				if (railwayTypeSlug !== typeFilter) return false;
			}

			if (isLetterFilterApplied && (letterFilter !== "All")) {
				const name = railway?.Name || "";
				const firstLetter = name.charAt(0).toUpperCase();
				if (firstLetter !== letterFilter) return false;
			}

			return true;
		});
	}, [isLetterFilterApplied, letterFilter, railways, typeFilter]);

	const railwaysWithDistance = useMemo(() => {
		const withDistance = filteredRailways.map(railway => {
			const coords = getRailwayLatLng(railway);
			const distance = targetLocation ? haversineMiles(targetLocation, coords) : null;
			return {...railway, __distance: distance};
		});

		if (isLetterFilterApplied) {
			return withDistance.sort((a, b) => {
				const aName = a?.Name || "";
				const bName = b?.Name || "";
				return aName.localeCompare(bName);
			});
		}

		if (!targetLocation) return withDistance;

		return withDistance.sort((a, b) => {
			if (a.__distance === null && b.__distance === null) return 0;
			if (a.__distance === null) return 1;
			if (b.__distance === null) return -1;
			return a.__distance - b.__distance;
		});
	}, [filteredRailways, isLetterFilterApplied, targetLocation]);

	const handleLetterFilterClick = useCallback(letter => {
		setLetterFilter(letter);
		setIsLetterFilterApplied(true);
	}, []);

	const mapMarkers = useMemo(() => {
		const dedupe = new Set();
		return railwaysWithDistance
			.map(railway => {
				const name = railway?.Name;
				const coords = getRailwayLatLng(railway);
				if (!name || !coords) return null;

				const markerId = resolveRailwayMapMarkerId(railway);
				if (dedupe.has(markerId)) return null;
				dedupe.add(markerId);

				return {
					id: markerId,
					name,
					railwayName: name,
					location: resolveRailwayLocation(railway),
					subname: resolveRailwayLocation(railway),
					typeLabel: resolveRailwayType(railway),
					distanceLabel:
						(typeof railway?.__distance === "number" && !Number.isNaN(railway.__distance)) ?
							formatDistanceMilesKm(railway.__distance) :
							"",
					position: [coords.lat, coords.lng],
					color: resolveRailwayTypeColor(railway),
					image: resolveRailwayMediaUrl(railway?.Logo),
					actionLabel: "View railway",
					link: `/railways/${slugify(name)}`
				};
			})
			.filter(Boolean);
	}, [railwaysWithDistance]);

	const mapBounds = useMemo(
		() => mapMarkers.map(m => m.position),
		[mapMarkers]
	);

	const mapLegendItems = useMemo(() => {
		const typeMap = new Map();
		railwaysWithDistance.forEach(railway => {
			const type = resolveRailwayType(railway);
			if (!type) return;
			if (!typeMap.has(type)) {
				typeMap.set(type, {
					label: type,
					color: resolveRailwayTypeColor(railway)
				});
			}
		});
		return Array.from(typeMap.values());
	}, [railwaysWithDistance]);

	const handleViewOnMap = useCallback(railway => {
		const name = railway?.Name;
		if (!name) return;

		const id = resolveRailwayMapMarkerId(railway);
		const marker = markerRefs.current[id];
		if (!marker) return;

		marker.openPopup();
		const map = mapRef.current;
		if (map) {
			map.setView(marker.getLatLng(), Math.max(map.getZoom(), 9), {
				animate: true
			});
		}
	}, []);

	const handleUseLocation = useCallback(() => {
		setPostcodeError("");
		requestCurrentLocation();
	}, [requestCurrentLocation]);

	const handlePostcodeLookup = useCallback(async () => {
		const place = normalizePostcode(postcode);
		if (!place) {
			setPostcodeError("Please enter a postcode.");
			return;
		}

		setLocationLoading(true);
		setPostcodeError("");

		try {
			const response = await fetch(`https://api.getthedata.com/postcode/${place}`);
			const data = await response.json();
			const latitude = Number(data?.data?.latitude);
			const longitude = Number(data?.data?.longitude);

			if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
				setPostcodeError("We could not find that postcode.");
				setLocationLoading(false);
				return;
			}

			setTargetLocation({lat: latitude, lng: longitude, label: place});
		}
		catch (error) {
			setPostcodeError("We could not look up that postcode.");
		}
		finally {
			setLocationLoading(false);
		}
	}, [postcode]);

	const handleToggleDetails = useCallback(id => {
		setExpandedId(prev => (prev === id ? null : id));
	}, []);

	const resolveNearbyRailways = useCallback(
		railway => {
			const nearbyIds = railway?.NearbyOrgs;
			if (!Array.isArray(nearbyIds) || !nearbyIds.length) return [];
			return nearbyIds
				.map(id => railways.find(
					item => String(item?.Id) === String(id)
				))
				.filter(Boolean);
		},
		[railways]
	);

	return (
		<Box
			component="main"
			sx={{
				flexGrow: 1,
				mx: {xs: 2, md: 3, lg: 5},
				mb: {xs: 2, md: 3, lg: 5},
				backgroundColor: "#fff",
				borderRadius: 2
			}}
		>
			{/* Leaflet popup styling (wins over module scoping issues) */}
			<GlobalStyles
				styles={{
					".leaflet-container .leaflet-popup-content-wrapper": {
						background: "#ffffff",
						color: "#111827",
						borderRadius: "14px",
						boxShadow: "0 12px 30px rgba(15, 23, 42, 0.25)",
						opacity: 1
					},
					".leaflet-container .leaflet-popup-tip": {
						background: "#ffffff",
						opacity: 1
					},
					".leaflet-container .leaflet-popup-content": {
						margin: "12px 16px"
					},
					".leaflet-container .leaflet-popup": {
						opacity: 1,
						filter: "none"
					}
				}}
			/>

			<Container maxWidth={false} sx={{py: {xs: 4, md: 6}}}>
				{/* Header / Breadcrumbs */}
				<Box sx={{px: {xs: 2, md: 4, lg: 6}}}>
					<Breadcrumbs sx={{textTransform: "uppercase", color: "#e2b534"}}>
						<Button label="Home" uri="/" style={{color: "#e2b534"}} variant="text" />
						<Typography color="#e2b534">Railways</Typography>
					</Breadcrumbs>

					<Typography
						variant="h4"
						sx={{mt: 2, fontWeight: 800, textTransform: "uppercase"}}
					>
						Railways
					</Typography>

				</Box>

				{/* Filters row (text left, controls right) */}
				<Box sx={{px: {xs: 2, md: 4, lg: 6}, mt: 2}}>
					<Grid container spacing={3} alignItems="center">
						<Grid item xs={12} md={6}>
							<Typography sx={{mt: {xs: 1, md: 0}}}>
								250+ heritage railways in the UK for a great family Railday out!
							</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<Box sx={filterRowSx}>
								<Box sx={{width: {xs: "100%", md: 220}}}>
									<Select
										options={[
											{label: "All", value: "All"},
											...typeOptions.map(type => ({label: type.name, value: type.slug}))
										]}
										size="small"
										value={typeFilter}
										onChange={setTypeFilter}
										style={{...filterControlStyle, width: "100%"}}
									/>
								</Box>

								<Box
									component="form"
									onSubmit={event => {
										event.preventDefault();
										handlePostcodeLookup();
									}}
									sx={{
										display: "flex",
										flexDirection: "row",
										gap: 1,
										alignItems: "center"
									}}
								>
									<Box sx={{width: {xs: "100%", md: 220}}}>
										<TextField
											placeholder="Enter Postcode"
											value={postcode}
											onChange={value => setPostcode(value)}
											controlled={true}
											inputProps={{"aria-label": "Enter postcode", "style": {textAlign: "right"}}}
											size="small"
											style={{...filterControlStyle, width: "100%"}}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<IconButton
															onClick={handleUseLocation}
															disabled={locationLoading}
															size="small"
															label="Use my location"
															style={filterAdornmentButtonStyle}
															icon={LocationOnIcon} />
													</InputAdornment>
												),
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															disabled={locationLoading}
															size="small"
															label="Find nearby"
															onClick={handlePostcodeLookup}
															style={filterAdornmentButtonStyle}
															icon={MyLocationIcon} />
													</InputAdornment>
												)
											}}
										/>
									</Box>
								</Box>
							</Box>
						</Grid>
					</Grid>

					<Collapse in={!!postcodeError}>
						{postcodeError && (
							<Box sx={{mt: 2}}>
								<Alert severity="error">{postcodeError}</Alert>
							</Box>
						)}
					</Collapse>
				</Box>

				{/* Letter bar (like PHP) */}
				<Box sx={{backgroundColor: "#f1f5f9", mt: 4}}>
					<Container maxWidth={false} sx={{px: {xs: 2, md: 4, lg: 6}}}>
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								gap: 1,
								py: 2,
								justifyContent: "flex-start"
							}}
						>
							{LETTERS.map(letter => (
								<Button
									key={letter}
									label={letter}
									variant="text"
									noDefaultDimensions={true}
									onClick={() => handleLetterFilterClick(letter)}
									style={{
										color: (isLetterFilterApplied && (letterFilter === letter)) ? "#111827" : "#6b7280",
										borderBottom: "2px solid",
										borderColor: (isLetterFilterApplied && (letterFilter === letter)) ? "#3b82f6" : "transparent",
										borderRadius: 0,
										minWidth: 36,
										fontWeight: 700
									}}
								/>
							))}
						</Box>
					</Container>
				</Box>

				{railwaysError && !railwaysLoading && (
					<Box sx={{px: {xs: 2, md: 4, lg: 6}, mt: 3}}>
						<Alert severity="error">
							We could not load railways right now. Please try again shortly.
						</Alert>
					</Box>
				)}

				<Divider sx={{my: 4}} />

				{/* Content: list left (60%) + map right (40%) */}
				<Box
					sx={{
						px: {xs: 2, md: 4, lg: 6},
						display: {xs: "block", md: "flex"},
						gap: 3,
						alignItems: {md: "stretch"}
					}}
				>
					{/* LEFT: list */}
					<Box sx={{width: {md: "60%"}, pr: {md: 2}}}>
						<RaildaysScrollTopButton />

						{railwaysLoading && (
							<Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, color: "text.secondary"}}>
								<Loader size={18} />
								<Typography variant="body2">Loading latest railways...</Typography>
							</Stack>
						)}

						{!railwaysLoading && !railwaysWithDistance.length && (
							<Box
								sx={{
									p: {xs: 3, md: 4},
									borderRadius: 2,
									backgroundColor: "#fff",
									boxShadow: 2
								}}
							>
								<Typography align="center">
									No items match your criteria.
								</Typography>
							</Box>
						)}

						<Stack spacing={3}>
							{railwaysWithDistance.map((railway, railwayIndex) => {
								const name = railway?.Name || "Untitled railway";
								const slug = slugify(name);
								const id = resolveRailwayId(railway);
								const rowId = `${id}-${railwayIndex}`;
								const renderKey = `${id}-${slug}-${railwayIndex}`;

								const hero = resolveRailwayMediaUrl(
									railway?.Hero || railway?.Image
								);
								const logo = resolveRailwayMediaUrl(railway?.Logo);
								const intro = railway?.Intro || "";
								const location = resolveRailwayLocation(railway);
								const type = resolveRailwayType(railway);
								const railwayOutlineColor = (type ? resolveRailwayTypeColor(railway) : "") || "#d1d5db";

								const twitter = resolveSocialUrl("https://twitter.com/", railway?.Twitter);
								const facebook = resolveSocialUrl("https://facebook.com/", railway?.Facebook);
								const instagram = resolveSocialUrl("https://instagram.com/", railway?.Instagram);

								const distance = railway?.__distance;
								const address = railway?.Address;
								const website = resolveExternalUrl(railway?.Website);
								const phone = railway?.Phone;
								const nearby = resolveNearbyRailways(railway);
								const coords = getRailwayLatLng(railway);

								return (
									<Card
										key={renderKey}
										px={0}
										py={0}
										style={{
											backgroundColor: "#fff",
											borderRadius: 12,
											boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)",
											border: `2px solid ${railwayOutlineColor}`,
											overflow: "hidden"
										}}
									>
										{hero && (
											<Image
												alt={name}
												noError={true}
												noLoader={true}
												src={hero}
												style={{width: "100%", aspectRatio: "16 / 9"}}
												imgStyles={{width: "100%", height: "100%", display: "block"}} />
										)}

										<CardSection
											display="block"
											px={0}
											py={0}
											style={{padding: CARD_BODY_PADDING}}>
											<Stack
												direction={{xs: "column", md: "row"}}
												spacing={2}
												alignItems={{xs: "flex-start", md: "center"}}
												justifyContent="space-between"
											>
												<Stack direction="row" spacing={2} alignItems="center">
													{logo && <Box component="img" src={logo} alt="" sx={{height: 40}} />}
													<Box>
														<Typography variant="h5" sx={{fontWeight: 900, color: "#e2b534"}}>
															{name}
														</Typography>
														{location && (
															<Typography color="text.secondary" sx={{fontWeight: 700}}>
																{location}
															</Typography>
														)}
														{typeof distance === "number" && !Number.isNaN(distance) && (
															<Typography color="text.secondary" sx={{fontWeight: 600}}>
																{formatDistanceMilesKm(distance)}
															</Typography>
														)}
													</Box>
												</Stack>

												<Button label="View on Map" variant="outlined" onClick={() => handleViewOnMap(railway)} />
											</Stack>

											<Box sx={{mt: 3}}>
												<Typography>{intro}</Typography>
											</Box>

											{expandedId !== rowId && intro && (
												<Box
													sx={{
														mt: -2,
														height: 36,
														background:
															"linear-gradient(180deg, rgba(255,255,255,0) 0%, #fff 80%)",
														pointerEvents: "none"
													}}
												/>
											)}

											<Stack
												direction={{xs: "column", md: "row"}}
												spacing={2}
												justifyContent="space-between"
												sx={{mt: 3}}
											>
												<Button label="Visit Railway" uri={`/railways/${slug}`} variant="contained" />

												<Stack direction="row" spacing={1} alignItems="center">
													{type && (
														<Chip
															label={type}
															sx={{
																backgroundColor: resolveRailwayTypeColor(railway),
																color: getContrastText(resolveRailwayTypeColor(railway)),
																fontWeight: 800
															}}
														/>
													)}

													{twitter && (
														<Link uri={twitter}>
															<IconButton
																icon={XIcon}
																label="X"
																style={{border: "1px solid #e2b534", color: "#e2b534"}} />
														</Link>
													)}
													{facebook && (
														<Link uri={facebook}>
															<IconButton
																icon={FacebookIcon}
																label="Facebook"
																style={{border: "1px solid #e2b534", color: "#e2b534"}} />
														</Link>
													)}
													{instagram && (
														<Link uri={instagram}>
															<IconButton
																icon={InstagramIcon}
																label="Instagram"
																style={{border: "1px solid #e2b534", color: "#e2b534"}} />
														</Link>
													)}
												</Stack>
											</Stack>
										</CardSection>

										<Divider />

										<CardSection
											display="block"
											px={0}
											py={0}
											style={{padding: `16px ${CARD_BODY_PADDING} ${CARD_BODY_PADDING}`}}>
											<Stack spacing={2}>
												<Stack direction={{xs: "column", md: "row"}} spacing={2}>
													<Box sx={{minWidth: 140}}>
														<Typography variant="subtitle2" color="text.secondary">
															Location
														</Typography>
													</Box>
													<Typography>
														{[
															address?.Address,
															address?.City,
															address?.County,
															address?.Postcode,
															address?.Country
														]
															.filter(Boolean)
															.join(", ") || location}
													</Typography>
												</Stack>

												<Stack
													direction={{xs: "column", md: "row"}}
													spacing={2}
													alignItems={{xs: "flex-start", md: "center"}}
												>
													<Box sx={{minWidth: 140}}>
														<Typography variant="subtitle2" color="text.secondary">
															Distance
														</Typography>
													</Box>

													{typeof distance === "number" && !Number.isNaN(distance) ?
														(
															<Typography>{formatDistanceMilesKm(distance)}</Typography>
														) :
														targetLocation ?
															(
																<Typography color="text.secondary">Distance unavailable</Typography>
															) :
															(
																<Button
																	label="Use My Location"
																	variant="outlined"
																	size="small"
																	onClick={handleUseLocation}
																	disabled={locationLoading} />
															)}

													<Box sx={{ml: {md: "auto"}}}>
														<Button
															label={expandedId === rowId ? "Collapse" : "Expand"}
															variant="text"
															size="small"
															onClick={() => handleToggleDetails(rowId)}
															endIcon={
																expandedId === rowId ?
																	(
																		<KeyboardArrowUpIcon />
																	) :
																	(
																		<KeyboardArrowDownIcon />
																	)
															} />
													</Box>
												</Stack>
											</Stack>
										</CardSection>

										<Collapse in={expandedId === rowId} timeout="auto" unmountOnExit>
											<CardSection
												display="block"
												px={0}
												py={0}
												style={{padding: `0 ${CARD_BODY_PADDING} ${CARD_BODY_PADDING}`}}>
												<Stack spacing={2}>
													{type && (
														<Stack direction="row" spacing={2} alignItems="center">
															<Box sx={{minWidth: 140}}>
																<Typography variant="subtitle2" color="text.secondary">
																	Railway Type
																</Typography>
															</Box>
															<Stack direction="row" spacing={1} alignItems="center">
																<Box
																	sx={{
																		width: 14,
																		height: 14,
																		borderRadius: "50%",
																		backgroundColor: resolveRailwayTypeColor(railway)
																	}}
																/>
																<Typography>{type}</Typography>
															</Stack>
														</Stack>
													)}

													{website && (
														<Stack direction={{xs: "column", md: "row"}} spacing={2}>
															<Box sx={{minWidth: 140}}>
																<Typography variant="subtitle2" color="text.secondary">
																	Website
																</Typography>
															</Box>
															<Button label={website} uri={website} variant="text" />
														</Stack>
													)}

													{phone && (
														<Stack direction={{xs: "column", md: "row"}} spacing={2}>
															<Box sx={{minWidth: 140}}>
																<Typography variant="subtitle2" color="text.secondary">
																	Phone
																</Typography>
															</Box>
															<Typography>{phone}</Typography>
														</Stack>
													)}

													{nearby.length > 0 && (
														<Box>
															<Typography variant="subtitle2" color="text.secondary" sx={{mb: 2}}>
																What's Nearby?
															</Typography>

															<Grid container spacing={2}>
																{nearby.map((item, nearbyIndex) => {
																	const nearbyName = item?.Name || "Railway";
																	const nearbySlug = slugify(nearbyName);
																	const nearbyHero = resolveRailwayMediaUrl(
																		item?.Hero || item?.Image
																	);

																	const nearbyCoords = getRailwayLatLng(item);
																	const nearbyDistance =
																		coords && nearbyCoords ?
																			haversineMiles(coords, nearbyCoords) :
																			null;

																	return (
																		<Grid
																			item
																			xs={12}
																			sm={6}
																			lg={4}
																			key={`${resolveRailwayId(item)}-${nearbySlug}-${nearbyIndex}`}
																		>
																			<Card
																				outlined={true}
																				px={0}
																				py={0}
																				style={{
																					height: "100%",
																					display: "flex",
																					flexDirection: "column",
																					borderRadius: 10,
																					overflow: "hidden"
																				}}
																			>
																				{nearbyHero && (
																					<Image
																						alt={nearbyName}
																						noError={true}
																						noLoader={true}
																						src={nearbyHero}
																						style={{height: 120, width: "100%"}}
																						imgStyles={{width: "100%", height: "100%", display: "block"}} />
																				)}

																				<CardSection
																					display="block"
																					px={0}
																					py={0}
																					style={{flexGrow: 1, padding: 16}}>
																					<Typography variant="subtitle1" sx={{fontWeight: 800}}>
																						{nearbyName}
																					</Typography>

																					{typeof nearbyDistance === "number" &&
																						!Number.isNaN(nearbyDistance) && (
																						<Typography color="text.secondary" sx={{mb: 2}}>
																							{formatDistanceMilesKm(nearbyDistance)}
																						</Typography>
																					)}

																					<Button
																						label="Read more"
																						uri={`/railways/${nearbySlug}`}
																						size="small" />
																				</CardSection>
																			</Card>
																		</Grid>
																	);
																})}
															</Grid>
														</Box>
													)}
												</Stack>
											</CardSection>
										</Collapse>
									</Card>
								);
							})}
						</Stack>
					</Box>

					{/* RIGHT: map (fixed width, fixed when scrolling) */}
					<Box sx={{width: {md: "40%"}, alignSelf: "stretch"}}>
						<Box
							sx={{
								position: {md: "sticky"},
								top: {md: "var(--raildays-content-top)"},
								height: {md: MAP_HEIGHT},
								minHeight: {md: MAP_HEIGHT}
							}}
						>
							<RaildaysMap
								markers={mapMarkers}
								bounds={mapBounds}
								markerShape="circle"
								legendItems={mapLegendItems}
								height="100%"
								mapRef={mapRef}
								markerRefs={markerRefs}
							/>
						</Box>
					</Box>
				</Box>
			</Container>
		</Box>
	);
};

export default RaildaysRailwaysView;
